import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, doc, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../../sdk_initialization';
import { useAuth } from '../context/AuthContext';
import { getUserProfile } from '../services/profilesService';
import './Home.css';

const EMAIL_LOGS_COLLECTION = 'templateEmailLogs';

const toMillis = (value) => {
  try {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (value.seconds !== undefined && value.seconds !== null) {
      return (Number(value.seconds) * 1000) + Math.floor(Number(value.nanoseconds || 0) / 1000000);
    }
    const dt = new Date(value);
    const ms = dt.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  } catch {
    return 0;
  }
};

const formatDateTime = (value) => {
  const ms = toMillis(value);
  if (!ms) return '-';
  return new Date(ms).toLocaleString();
};

const normalizeSearchValue = (value) => String(value || '').trim().toLowerCase();

const chunk = (array, size) => {
  const output = [];
  for (let i = 0; i < array.length; i += size) {
    output.push(array.slice(i, i + size));
  }
  return output;
};

const EmailTemplateLogsAdmin = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedLogIds, setSelectedLogIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const role = String(userProfile?.userType || '').trim();
  const canManageLogs = role === 'Admin' || role === 'SystemAdmin' || role === 'EventAdmin';
  const loading = profileLoading || logsLoading;

  const loadLogs = async () => {
    setLogsLoading(true);
    setError('');
    try {
      const snapshot = await getDocs(collection(db, EMAIL_LOGS_COLLECTION));
      const rows = snapshot.docs.map((logDoc) => {
        const data = logDoc.data() || {};
        return {
          id: logDoc.id,
          createdAt: data.createdAt || null,
          status: String(data.status || '').trim() || 'unknown',
          toEmail: String(data.toEmail || '').trim(),
          templateNumber: String(data.templateNumber || '').trim(),
          templateName: String(data.templateName || '').trim(),
          senderName: String(data.senderName || '').trim(),
          senderUserId: String(data.senderUserId || '').trim(),
          batchRequestId: String(data.batchRequestId || '').trim(),
          message: String(data.message || '').trim(),
          error: String(data.error || '').trim(),
          messageId: data.messageId || null,
          recipientIndex: data.recipientIndex,
          skipped: Boolean(data.skipped),
        };
      });

      rows.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt));
      setLogs(rows);
      setSelectedLogIds(new Set());
    } catch (err) {
      const code = String(err?.code || '').toLowerCase();
      const message = String(err?.message || 'Failed to load email logs.');
      const isPermissionError = code.includes('permission-denied') || message.toLowerCase().includes('missing or insufficient permissions');
      setError(isPermissionError
        ? 'Missing or insufficient permissions while reading templateEmailLogs.'
        : message);
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    if (!currentUser?.uid) {
      setProfileLoading(false);
      return;
    }

    (async () => {
      setProfileLoading(true);
      setError('');
      try {
        const profile = await getUserProfile(currentUser.uid);
        setUserProfile(profile || null);
      } catch {
        setUserProfile(null);
      } finally {
        setProfileLoading(false);
      }
    })();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (profileLoading) return;
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!canManageLogs) {
      navigate('/');
      return;
    }
    loadLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoading, currentUser, canManageLogs, navigate]);

  const templateOptions = useMemo(() => {
    const seen = new Set();
    const options = [];

    logs.forEach((row) => {
      const number = String(row.templateNumber || '').trim();
      if (!number || seen.has(number)) return;
      seen.add(number);
      options.push({
        number,
        name: String(row.templateName || '').trim(),
      });
    });

    options.sort((a, b) => a.number.localeCompare(b.number, undefined, { numeric: true, sensitivity: 'base' }));
    return options;
  }, [logs]);

  const filteredLogs = useMemo(() => {
    const needle = normalizeSearchValue(searchText);
    return logs.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (templateFilter !== 'all' && row.templateNumber !== templateFilter) return false;
      if (!needle) return true;

      const haystack = [
        row.toEmail,
        row.templateNumber,
        row.templateName,
        row.senderName,
        row.senderUserId,
        row.batchRequestId,
        row.message,
        row.error,
        row.messageId,
        row.status,
      ].map((part) => normalizeSearchValue(part)).join(' ');

      return haystack.includes(needle);
    });
  }, [logs, searchText, statusFilter, templateFilter]);

  const sortedLogs = useMemo(() => {
    const rows = [...filteredLogs];
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

    rows.sort((left, right) => {
      let compare = 0;

      if (sortField === 'createdAt') {
        compare = toMillis(left.createdAt) - toMillis(right.createdAt);
      } else if (sortField === 'status') {
        compare = String(left.status || '').localeCompare(String(right.status || ''), undefined, { sensitivity: 'base' });
      } else if (sortField === 'toEmail') {
        compare = String(left.toEmail || '').localeCompare(String(right.toEmail || ''), undefined, { sensitivity: 'base' });
      } else if (sortField === 'template') {
        const leftNumber = String(left.templateNumber || '');
        const rightNumber = String(right.templateNumber || '');
        compare = leftNumber.localeCompare(rightNumber, undefined, { numeric: true, sensitivity: 'base' });
        if (compare === 0) {
          compare = String(left.templateName || '').localeCompare(String(right.templateName || ''), undefined, { sensitivity: 'base' });
        }
      } else if (sortField === 'sender') {
        const leftSender = String(left.senderName || left.senderUserId || '');
        const rightSender = String(right.senderName || right.senderUserId || '');
        compare = leftSender.localeCompare(rightSender, undefined, { sensitivity: 'base' });
      } else if (sortField === 'batchRequestId') {
        compare = String(left.batchRequestId || '').localeCompare(String(right.batchRequestId || ''), undefined, { sensitivity: 'base' });
      }

      if (compare === 0) {
        compare = toMillis(right.createdAt) - toMillis(left.createdAt);
      }

      return compare * directionMultiplier;
    });

    return rows;
  }, [filteredLogs, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection(field === 'createdAt' ? 'desc' : 'asc');
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const selectedCount = selectedLogIds.size;
  const allFilteredSelected = filteredLogs.length > 0 && filteredLogs.every((row) => selectedLogIds.has(row.id));

  const toggleSelectAllFiltered = () => {
    setSelectedLogIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredLogs.forEach((row) => next.delete(row.id));
      } else {
        filteredLogs.forEach((row) => next.add(row.id));
      }
      return next;
    });
  };

  const toggleSelectRow = (logId) => {
    setSelectedLogIds((prev) => {
      const next = new Set(prev);
      if (next.has(logId)) next.delete(logId);
      else next.add(logId);
      return next;
    });
  };

  const handleDeleteSelected = async () => {
    if (deleting || selectedCount === 0) return;
    const confirmText = selectedCount === 1
      ? 'Delete selected email log? This cannot be undone.'
      : `Delete ${selectedCount} selected email logs? This cannot be undone.`;
    if (!window.confirm(confirmText)) return;

    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      const selectedIds = Array.from(selectedLogIds);
      const idChunks = chunk(selectedIds, 450);

      for (const idChunk of idChunks) {
        const batch = writeBatch(db);
        idChunk.forEach((id) => {
          batch.delete(doc(db, EMAIL_LOGS_COLLECTION, id));
        });
        await batch.commit();
      }

      setSuccess(selectedCount === 1
        ? 'Deleted 1 email log.'
        : `Deleted ${selectedCount} email logs.`);
      await loadLogs();
    } catch (err) {
      const code = String(err?.code || '').toLowerCase();
      const message = String(err?.message || 'Failed to delete selected email logs.');
      const isPermissionError = code.includes('permission-denied') || message.toLowerCase().includes('missing or insufficient permissions');
      setError(isPermissionError
        ? 'Missing or insufficient permissions while deleting templateEmailLogs.'
        : message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="home-container">
      <div className="user-card">
        <div className="home-header" style={{ marginBottom: '1rem' }}>
          <h2 className="profile-title" style={{ margin: 0 }}>Admin: Email Logs</h2>
          <div className="admin-actions" style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="back-btn" onClick={() => navigate('/admin/email-templates')}>Back to Email Templates</button>
            <button type="button" className="new-layout-btn" onClick={loadLogs} disabled={loading || deleting}>Refresh</button>
            <button type="button" className="cancel-btn" onClick={() => navigate('/admin/email-templates')}>Close</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1.6fr) minmax(280px, 1fr) minmax(240px, 0.8fr)', gap: 10, alignItems: 'center', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ marginBottom: 0, minWidth: 58, fontWeight: 700, textAlign: 'left' }}>Search</label>
            <input
              className="layout-name-input"
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by email, template, sender, batch, message, error"
              style={{ marginBottom: 0, flex: 1 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ marginBottom: 0, minWidth: 78, fontWeight: 700, textAlign: 'left' }}>Template #</label>
            <select
              className="layout-name-input"
              value={templateFilter}
              onChange={(e) => setTemplateFilter(e.target.value)}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <option value="all">all templates</option>
              {templateOptions.map((option) => (
                <option key={option.number} value={option.number}>
                  {`#${option.number} - ${option.name || 'Unnamed Template'}`}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ marginBottom: 0, minWidth: 52, fontWeight: 700, textAlign: 'left' }}>Status</label>
            <select
              className="layout-name-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ marginBottom: 0, flex: 1 }}
            >
              <option value="all">all</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
              <option value="skipped">skipped</option>
              <option value="unknown">unknown</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <button
            type="button"
            className="open-btn"
            onClick={toggleSelectAllFiltered}
            disabled={filteredLogs.length === 0 || deleting}
            style={{ minHeight: 40 }}
          >
            {allFilteredSelected ? 'Unselect Filtered' : 'Select Filtered'}
          </button>
          <button
            type="button"
            className="delete-btn"
            onClick={handleDeleteSelected}
            disabled={selectedCount === 0 || deleting}
            style={{ minHeight: 40 }}
          >
            {deleting ? 'Deleting...' : `Delete Selected (${selectedCount})`}
          </button>
        </div>

        {error && <div className="error-state" style={{ marginBottom: 12 }}><h4>{error}</h4></div>}
        {success && <div style={{ marginBottom: 12, color: '#155724', background: '#d4edda', border: '1px solid #c3e6cb', borderRadius: 8, padding: 10 }}>{success}</div>}

        <div className="layouts-table-wrapper">
          <div>
            <table className="events-table layouts-table" style={{ minWidth: 1450 }}>
              <thead>
                <tr>
                  <th style={{ width: 48 }}>Sel</th>
                  <th className={`sortable-header ${sortField === 'createdAt' ? 'active' : ''}`} style={{ minWidth: 180 }} onClick={() => handleSort('createdAt')}>Created <span className="sort-indicator">{sortIndicator('createdAt')}</span></th>
                  <th className={`sortable-header ${sortField === 'status' ? 'active' : ''}`} style={{ minWidth: 90 }} onClick={() => handleSort('status')}>Status <span className="sort-indicator">{sortIndicator('status')}</span></th>
                  <th className={`sortable-header ${sortField === 'toEmail' ? 'active' : ''}`} style={{ minWidth: 240 }} onClick={() => handleSort('toEmail')}>To Email <span className="sort-indicator">{sortIndicator('toEmail')}</span></th>
                  <th className={`sortable-header ${sortField === 'template' ? 'active' : ''}`} style={{ minWidth: 180 }} onClick={() => handleSort('template')}>Template <span className="sort-indicator">{sortIndicator('template')}</span></th>
                  <th className={`sortable-header ${sortField === 'sender' ? 'active' : ''}`} style={{ minWidth: 180 }} onClick={() => handleSort('sender')}>Sender <span className="sort-indicator">{sortIndicator('sender')}</span></th>
                  <th className={`sortable-header ${sortField === 'batchRequestId' ? 'active' : ''}`} style={{ minWidth: 170 }} onClick={() => handleSort('batchRequestId')}>Batch Request ID <span className="sort-indicator">{sortIndicator('batchRequestId')}</span></th>
                  <th style={{ minWidth: 200 }}>Message / Error</th>
                </tr>
              </thead>
              <tbody>
                {sortedLogs.length > 0 ? sortedLogs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedLogIds.has(log.id)}
                        onChange={() => toggleSelectRow(log.id)}
                        disabled={deleting}
                      />
                    </td>
                    <td>{formatDateTime(log.createdAt)}</td>
                    <td>
                      <span style={{
                        display: 'inline-block',
                        borderRadius: 999,
                        padding: '3px 10px',
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        color: '#fff',
                        background: log.status === 'sent' ? '#1f7a3d' : (log.status === 'failed' ? '#b42318' : (log.status === 'skipped' ? '#7a5e00' : '#6b7280')),
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td>{log.toEmail || '-'}</td>
                    <td>
                      <div>{log.templateName || '-'}</div>
                      <small style={{ color: '#666', display: 'block' }}>#{log.templateNumber || '-'}</small>
                    </td>
                    <td>
                      <div>{log.senderName || '-'}</div>
                      <small style={{ color: '#666', display: 'block' }}>{log.senderUserId || '-'}</small>
                    </td>
                    <td>
                      <div>{log.batchRequestId || '-'}</div>
                      <small style={{ color: '#666', display: 'block' }}>msgId: {log.messageId || '-'}</small>
                    </td>
                    <td>
                      {log.error ? (
                        <div style={{ color: '#b42318' }}>{log.error}</div>
                      ) : (
                        <div>{log.message || '-'}</div>
                      )}
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={8}>{loading ? 'Loading email logs...' : 'No email logs found.'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateLogsAdmin;
